import {
  useEffect,
  useRef,
  useState,
  type AnchorHTMLAttributes,
  type ReactNode,
} from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Text } from '@/ui'
import { AdviserRole } from '../lib/constants'
import { TypingIndicator } from './TypingIndicator'

interface MessageBubbleProps {
  role: `${AdviserRole}`
  content: string
  isStreaming?: boolean
  children?: ReactNode
}

// Strip [source:N] markers — sources are shown in CitationCard at the bottom
const stripSourceMarkers = (text: string) =>
  text.replace(/\s*\[source:\d+\]/g, '')

// Cross-origin http(s) links must open in a new tab so users don't lose
// Haven context — back-button on the Heard tab otherwise lands them on a
// stale page or a fresh chat.
const isExternalHref = (href: string | undefined): boolean => {
  if (!href || !/^https?:\/\//i.test(href)) return false
  try {
    return new URL(href).origin !== window.location.origin
  } catch {
    return false
  }
}

const markdownComponents = {
  a: ({
    href,
    children,
    ...rest
  }: {
    href?: string
    children?: ReactNode
  } & AnchorHTMLAttributes<HTMLAnchorElement>) =>
    isExternalHref(href) ? (
      <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    ) : (
      <a href={href} {...rest}>
        {children}
      </a>
    ),
}

// Reveal text a fraction of the backlog per frame so the visible prefix
// trails the stream by ~SMOOTH_FRAMES frames (≈400ms at 60fps). The step is
// clamped so we keep a steady ChatGPT-like cadence: never under MIN_STEP
// (≈60cps floor) and never over MAX_STEP (≈180cps ceiling) even with a huge
// backlog. The horizon means we still drain reliably after the stream ends.
const SMOOTH_FRAMES = 24
const MIN_STEP = 1
const MAX_STEP = 3

export const MessageBubble = ({
  role,
  content,
  isStreaming,
  children,
}: MessageBubbleProps) => {
  const isUser = role === AdviserRole.User
  const displayContent = isUser ? content : stripSourceMarkers(content)
  const hasText = displayContent.trim().length > 0

  // History-loaded assistant messages render immediately. Once a message has
  // streamed, keep the smoothing layer active even after isStreaming flips
  // false so the tail drains instead of snapping to the final length.
  const everStreamedRef = useRef(false)
  if (isStreaming) everStreamedRef.current = true
  const animate = !isUser && everStreamedRef.current

  const [shownLen, setShownLen] = useState(animate ? 0 : displayContent.length)
  // Mirror of shownLen so the raf tick can make its catch-up check
  // synchronously — React's setState updaters can be deferred during
  // reconciliation, so we can't rely on side-effects inside the updater
  // to decide whether to schedule the next frame.
  const shownLenRef = useRef(shownLen)
  const targetRef = useRef(displayContent)
  targetRef.current = displayContent
  const isStreamingRef = useRef(Boolean(isStreaming))
  isStreamingRef.current = Boolean(isStreaming)

  useEffect(() => {
    if (!animate) {
      if (shownLenRef.current !== displayContent.length) {
        shownLenRef.current = displayContent.length
        setShownLen(displayContent.length)
      }
      return undefined
    }

    let raf = 0
    let cancelled = false
    const tick = () => {
      if (cancelled) return
      const target = targetRef.current.length
      const prev = shownLenRef.current
      if (prev < target) {
        const remaining = target - prev
        const step = Math.min(
          MAX_STEP,
          Math.max(MIN_STEP, Math.ceil(remaining / SMOOTH_FRAMES))
        )
        const next = Math.min(prev + step, target)
        shownLenRef.current = next
        setShownLen(next)
      }
      // Re-schedule while there's still work to do or the stream is open.
      if (
        shownLenRef.current < targetRef.current.length ||
        isStreamingRef.current
      ) {
        raf = requestAnimationFrame(tick)
      }
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
    }
  }, [animate, isStreaming, displayContent])

  const visibleContent = animate
    ? displayContent.slice(0, shownLen)
    : displayContent
  // The typewriter reveal keeps running after isStreaming flips false, so a
  // tool card in children waits for it too. Hidden rather than unmounted, so
  // widgets keep their state and cards do not refetch.
  const textCaughtUp = !animate || shownLen >= displayContent.length

  if (isUser) {
    return (
      <div className="flex justify-end pl-[88px]">
        <div className="bg-[#f4f9f4] border border-moss-25 rounded-tl-[16px] rounded-bl-[16px] rounded-br-[16px] rounded-tr-[4px] p-3 text-sm leading-relaxed text-neutral-600 overflow-clip min-w-0">
          <Text className="whitespace-pre-wrap break-words">
            {displayContent}
          </Text>
          {children}
        </div>
      </div>
    )
  }

  // Assistant messages with no text (card-only tool responses): skip bubble
  // chrome. Not keyed on streaming, or a card narrows when its turn finishes.
  const bare = !hasText
  // Streaming but nothing revealed yet → typing indicator. Gating on the
  // visible prefix (not on hasText) keeps the dots up between "first chunk
  // received" and "first raf tick reveals it", which would otherwise flash
  // an empty markdown bubble for a frame.
  const typing = Boolean(isStreaming) && visibleContent.trim().length === 0

  // One tree for every state, so a widget in `children` keeps its state when
  // streaming starts or stops instead of remounting.
  return (
    <div className={bare ? undefined : 'flex items-start'}>
      <div className={bare ? 'w-full' : 'flex flex-col gap-1 flex-1 min-w-0'}>
        {!bare && !typing && (
          <div className="text-sm leading-relaxed text-neutral-600 w-full">
            <div className="adviser-markdown">
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {visibleContent}
              </Markdown>
            </div>
          </div>
        )}
        <div className="contents" hidden={!textCaughtUp}>
          {children}
        </div>
        {typing && (
          <div className="flex items-start">
            <TypingIndicator />
          </div>
        )}
      </div>
    </div>
  )
}
